/* Los estilos de un Excel → el formato de FiboSheets (fondo, color de letra,
 * negrita, itálica, subrayado y alineación).
 *
 * ── POR QUÉ ────────────────────────────────────────────────────────────────
 *
 * Importar traía los números y las fórmulas pero dejaba la planilla «pelada»:
 * sin el amarillo de los encabezados, sin las negritas de los totales. La
 * persona la tenía que volver a pintar a mano para reconocerla.
 *
 * ── QUÉ LLEGA DE SHEETJS (LA VERSIÓN LIBRE, 0.18.5) ───────────────────────
 *
 * Con `cellStyles: true`, a cada celda le pone en `cell.s` SÓLO EL RELLENO
 * (`patternType`, `fgColor`, `bgColor`, con los colores del tema ya
 * resueltos). La fuente (negrita, itálica, color de letra) y la alineación no
 * llegan a la celda: SheetJS las lee del styles.xml (`wb.Styles.Fonts`,
 * `wb.Styles.CellXf`) pero no anota qué estilo usa cada celda.
 *
 * Así que, para un .xlsx, se lee con `bookFiles: true` (que deja a mano los
 * archivos de adentro del zip) y se busca en el XML de cada hoja el atributo
 * `s` de cada `<c>`: ése es el índice en `CellXf`, que dice qué fuente y qué
 * alineación tiene. Es una lectura chiquita con expresiones regulares —sólo
 * los atributos `r` y `s` de las celdas—, no un parser de XML entero.
 *
 * Para un .xls (el Excel viejo, binario) SheetJS sólo expone el relleno de
 * cada celda (ver `process_cell_style` en su código): se traen los fondos y
 * se avisa que la negrita, los colores de letra y la alineación no vienen. Un
 * CSV no tiene estilos.
 *
 * Para un .xlsb (el Excel binario) no viene nada: sus hojas no son XML
 * (`sheet1.bin`) y SheetJS libre no lee los rellenos del styles.bin ni anota
 * en `CellXf` qué fuente usa cada estilo (sólo el formato de número). Se
 * traen los valores, los formatos de número, los anchos y las combinadas, y
 * se avisa que el resto no vino SI el libro tenía formato (ver
 * `hayFormatoSinLeer`): uno con sólo valores y fechas no pierde nada.
 *
 * ── LOS COLORES: LA PALETA DEL HUB ES FIJA ─────────────────────────────────
 *
 * La planilla del hub no pinta con cualquier color: tiene una paleta chica
 * (`shared/paletaPlanilla`), de fondos pastel que se leen igual en el tema
 * claro y en el oscuro. Un color de Excel se lleva al de la paleta MÁS
 * PARECIDO, por TONO (el ángulo del color: rojo, naranja, amarillo…) y no por
 * distancia RGB: el azul oscuro de un encabezado de Excel (#1F4E78) está «más
 * cerca» en RGB del gris que del celeste pastel, pero quien lo ve dice «azul».
 *
 *   · Fondos: blanco (o casi) → sin fondo (en Excel se usa para tapar la
 *     cuadrícula); grises → el gris de la paleta; el resto → el pastel de tono
 *     más cercano (rojo y rosa → rosa; naranja → naranja; amarillo →
 *     amarillo; verde → verde; celeste, azul y violeta → azul o rosa, el que
 *     quede más cerca).
 *   · Letra: negro, blanco o casi → la de siempre (una letra blanca sobre un
 *     azul oscuro queda oscura sobre el celeste pastel, que es lo que se lee);
 *     grises → el gris; el resto → rojo, verde, azul o violeta, el de tono
 *     más cercano (naranja y amarillo → rojo).
 *
 * Si algún color no era exactamente uno de la paleta, el aviso de después de
 * importar lo dice.
 *
 * ── LO QUE NO SE TRAE (Y SE AVISA) ─────────────────────────────────────────
 *
 * El formato de la hoja no tiene bordes, tamaño de letra, tachado, ajuste de
 * texto, texto girado ni alineación justificada: esas celdas se cuentan y el
 * aviso dice cuántas. El formato condicional y las listas desplegables de un
 * Excel tampoco se leen (ver `shared/planillaExcel`), pero ahora se cuentan
 * para avisar. La alineación vertical, la tipografía y los estilos de filas o
 * columnas enteras se ignoran sin avisar: no cambian lo que se lee.
 */

import { FONDOS, TEXTOS } from "./paletaPlanilla";

/* ── Colores ────────────────────────────────────────────────────────────── */

function rgbDe(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || "").trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/* Tono (0–360), saturación y luz (0–1), como el selector de colores. */
function hslDe(hex) {
  const rgb = rgbDe(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map((x) => x / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: h * 60, s, l };
}

const distanciaTono = (a, b) => { const d = Math.abs(a - b) % 360; return d > 180 ? 360 - d : d; };

/* De los colores de la paleta, el de tono más cercano (sin los grises). */
function porTono(h, paleta) {
  let mejor = "";
  let dist = Infinity;
  for (const c of paleta) {
    const x = c && hslDe(c);
    if (!x || x.s < 0.15) continue;
    const d = distanciaTono(h, x.h);
    if (d < dist) { dist = d; mejor = c; }
  }
  return mejor;
}

const GRIS_FONDO = FONDOS.find((c) => { const x = c && hslDe(c); return x && x.s < 0.15; }) || "";
const GRIS_TEXTO = TEXTOS.find((c) => { const x = c && hslDe(c); return x && x.s < 0.15; }) || "";

/* El color de la paleta de fondos más parecido a `hex` ("" = sin fondo), y si
   era exactamente ése. */
export function fondoMasCercano(hex) {
  const x = hslDe(hex);
  if (!x) return { color: "", exacto: true };
  const propio = `#${rgbDe(hex).map((n) => n.toString(16).padStart(2, "0")).join("")}`;
  let color;
  if (x.l >= 0.96) color = ""; // blanco: en Excel, tapar la cuadrícula
  else if (x.s < 0.15) color = GRIS_FONDO;
  else color = porTono(x.h, FONDOS);
  return { color, exacto: color === propio || (color === "" && propio === "#ffffff") };
}

/* Lo mismo para el color de letra ("" = la de siempre). */
export function textoMasCercano(hex) {
  const x = hslDe(hex);
  if (!x) return { color: "", exacto: true };
  const propio = `#${rgbDe(hex).map((n) => n.toString(16).padStart(2, "0")).join("")}`;
  let color;
  if (x.s < 0.15 || x.l < 0.08 || x.l > 0.94) {
    color = x.l < 0.25 || x.l > 0.75 ? "" : GRIS_TEXTO;
  } else color = porTono(x.h, TEXTOS);
  return { color, exacto: color === propio || (color === "" && (propio === "#000000" || x.l < 0.08)) };
}

/* ── Los colores tal como vienen en el archivo ──────────────────────────── */

/* Los 16 primeros de la paleta indexada de Excel (la de los .xls). SheetJS
   arma el hex de los indexados sin ceros a la izquierda ("ff00" por rojo),
   así que con el índice a mano se usa esta tabla. */
const INDEXADOS = [
  "000000", "FFFFFF", "FF0000", "00FF00", "0000FF", "FFFF00", "FF00FF", "00FFFF",
  "000000", "FFFFFF", "FF0000", "00FF00", "0000FF", "FFFF00", "FF00FF", "00FFFF",
  "800000", "008000", "000080", "808000", "800080", "008080", "C0C0C0", "808080",
];

/* Un color del tema aclarado u oscurecido (`tint`), como lo hace Excel. */
function conTinte(hex, tint) {
  const rgb = rgbDe(hex);
  if (!rgb || !tint) return hex;
  const x = hslDe(hex);
  const l = tint < 0 ? x.l * (1 + tint) : x.l * (1 - tint) + tint;
  // De HSL a RGB con la luz nueva.
  const q = l < 0.5 ? l * (1 + x.s) : l + x.s - l * x.s;
  const p = 2 * l - q;
  const canal = (t0) => {
    let t = t0;
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const h = x.h / 360;
  const out = x.s === 0 ? [l, l, l] : [canal(h + 1 / 3), canal(h), canal(h - 1 / 3)];
  return out.map((v) => Math.round(v * 255).toString(16).padStart(2, "0")).join("");
}

/* Un CT_Color de SheetJS (`{ rgb, theme, tint, index, auto }`) → "#rrggbb" o
   null si no dice un color concreto. */
function hexDeColor(color, wb) {
  if (!color || color.auto) return null;
  if (color.index != null) {
    const i = Number(color.index);
    return INDEXADOS[i] ? `#${INDEXADOS[i]}` : null; // 64 y 65: los del sistema
  }
  if (typeof color.rgb === "string" && /^[0-9a-f]{6}$/i.test(color.rgb)) return `#${color.rgb}`;
  if (color.theme != null) {
    const esquema = wb && wb.Themes && wb.Themes.themeElements && wb.Themes.themeElements.clrScheme;
    const base = esquema && esquema[color.theme] && esquema[color.theme].rgb;
    return base ? `#${conTinte(base, color.tint || 0)}` : null;
  }
  return null;
}

/* ── Lo que hay adentro del .xlsx ───────────────────────────────────────── */

/* Los bytes de un archivo del zip, como texto. Sólo se buscan etiquetas y
   atributos (todo ASCII), así que alcanza con un byte = una letra. */
function aTexto(contenido) {
  if (typeof contenido === "string") return contenido;
  if (!contenido || contenido.length == null) return "";
  let s = "";
  const paso = 0x8000;
  for (let i = 0; i < contenido.length; i += paso) {
    s += String.fromCharCode.apply(null, Array.prototype.slice.call(contenido, i, i + paso));
  }
  return s;
}

const atributo = (etiqueta, nombre) => {
  const m = new RegExp(`\\s${nombre}="([^"]*)"`).exec(etiqueta);
  return m ? m[1] : null;
};

function etiquetas(xml, re) {
  const out = [];
  const g = new RegExp(re.source, "g");
  let m = g.exec(xml);
  while (m) { out.push(m[0]); m = g.exec(xml); }
  return out;
}

/* Lo que se lee una vez por libro: dónde está el XML de cada hoja y qué
   bordes son de verdad (SheetJS los lee vacíos). */
const porLibro = new WeakMap();
function datosDelZip(wb) {
  if (porLibro.has(wb)) return porLibro.get(wb);
  const files = wb && wb.files;
  let datos = null;
  if (files && typeof files === "object") {
    const leer = (p) => {
      const f = files[p] || files[`/${p}`];
      return f ? aTexto(f.content) : "";
    };
    const destino = new Map();
    for (const rel of etiquetas(leer("xl/_rels/workbook.xml.rels"), /<Relationship\b[^>]*>/)) {
      const id = atributo(rel, "Id");
      const t = atributo(rel, "Target");
      if (id && t) destino.set(id, t);
    }
    let rutas = etiquetas(leer("xl/workbook.xml"), /<sheet\b[^>]*>/).map((h) => {
      const t = destino.get(atributo(h, "r:id")) || "";
      return t.startsWith("/") ? t.slice(1) : `xl/${t.replace(/^\.\//, "")}`;
    });
    if (!rutas.length && wb.Directory && Array.isArray(wb.Directory.sheets)) {
      rutas = wb.Directory.sheets.map((p) => String(p).replace(/^\//, ""));
    }
    // Las hojas de un .xlsb son binarias (`sheet1.bin`): leerlas como texto
    // buscando etiquetas no tiene sentido. Se saltea sólo el .bin, no todo lo
    // que no termine en .xml: OOXML deja que la hoja de un .xlsx se llame de
    // otra forma, y ésa sí hay que leerla.
    rutas = rutas.map((p) => (/\.bin$/i.test(p) ? "" : p));
    const estilos = leer("xl/styles.xml");
    const seccion = /<borders\b[^>]*>([\s\S]*?)<\/borders>/.exec(estilos);
    const bordes = seccion
      ? (seccion[1].match(/<border\b[\s\S]*?(?:<\/border>|\/>)/g) || []).map((b) => /\sstyle="(?!none")/.test(b))
      : [];
    datos = { leer, rutas, bordes };
  }
  porLibro.set(wb, datos);
  return datos;
}

/* ¿El libro tenía formato de celdas (fondos, fuentes, alineación) que no se
   pudo leer? Es para el .xlsb, donde SheetJS libre deja en `wb.Styles` sólo
   las fuentes y, de cada estilo de celda, el formato de número. Sí, si hay
   más de una fuente (alguien puso una negrita, un color de letra, otro
   tamaño) o si dos estilos tienen el mismo formato de número: algo más los
   distingue (un fondo, un borde, la alineación). Un libro con sólo valores,
   números y fechas tiene una fuente y un estilo por formato. */
export function hayFormatoSinLeer(wb) {
  const styles = wb && wb.Styles;
  if (!styles) return false;
  const fuentes = Array.isArray(styles.Fonts) ? styles.Fonts.length : 0;
  const xfs = Array.isArray(styles.CellXf) ? styles.CellXf : [];
  const formatos = new Set(xfs.map((x) => (x ? x.numFmtId : null)));
  return fuentes > 1 || xfs.length > formatos.size;
}

/* ── Una hoja ───────────────────────────────────────────────────────────── */

const ALINEACION = { left: "left", center: "center", centerContinuous: "center", right: "right" };

/* Lo que no entra en el formato de la hoja, con el nombre que va en el aviso. */
export const NO_SOPORTADO = {
  bordes: "bordes",
  tamano: "tamaño de letra",
  tachado: "tachado",
  ajuste: "ajuste de texto",
  girado: "texto girado",
  justificado: "alineación justificada",
  trama: "rellenos con trama",
};

/* Si la celda muestra algo: un valor o una fórmula. Las que llegan sin nada
   (no están, o son un «stub» `t: "z"` de las que sólo tienen estilo) o con un
   texto vacío, no. */
function tieneValor(celda) {
  if (!celda) return false;
  if (celda.f) return true;
  if (celda.t === "z") return false;
  return celda.v != null && celda.v !== "";
}

/* El formato de las celdas de una hoja desde sus estilos de Excel. `indice`
   es su lugar en el libro; `limites` = `{ filas, cols }`, lo que se importa.
   Devuelve `{ formato, aproximados, faltan, condicional, validacion,
   soloFondos }`: `formato` por referencia ("B3") con las mismas claves que
   el formato a mano; `aproximados`, cuántas celdas cambiaron de color para
   entrar en la paleta; `faltan`, cuántas celdas tenían algo que no se trae
   (por clave de NO_SOPORTADO). Las celdas vacías sólo traen el fondo (lo
   demás no se ve), y los conteos son sólo de lo que se trae. */
export function leerEstilosHoja(XLSX, wb, indice, ws, limites) {
  const formato = {};
  const faltan = {};
  let aproximados = 0;
  const contar = (k) => { faltan[k] = (faltan[k] || 0) + 1; };
  const zip = datosDelZip(wb);
  const xml = zip && zip.rutas[indice] ? zip.leer(zip.rutas[indice]) : "";
  const styles = wb && wb.Styles;
  const conXf = !!(xml && styles && Array.isArray(styles.CellXf) && Array.isArray(styles.Fonts));
  const fuenteBase = conXf ? styles.Fonts[0] || {} : {};

  const una = (r, c, celda, xf) => {
    if (r >= limites.filas || c >= limites.cols) return;
    const vacia = !tieneValor(celda);
    const f = {};
    let aprox = false;
    // El relleno: el que SheetJS dejó en la celda (anda también en .xls), o
    // el del estilo si la celda no llegó (una vacía sin `sheetStubs`).
    const relleno = (celda && celda.s && celda.s.patternType ? celda.s : null)
      || (xf && styles.Fills && styles.Fills[xf.fillId]) || null;
    if (relleno && relleno.patternType && relleno.patternType !== "none") {
      const hex = hexDeColor(relleno.fgColor, wb) || hexDeColor(relleno.bgColor, wb);
      if (hex) {
        const { color, exacto } = fondoMasCercano(hex);
        if (color) f.bg = color;
        if (!exacto) aprox = true;
        if (relleno.patternType !== "solid" && (color || !vacia)) contar("trama");
      }
    }
    // Una vacía sólo se queda con el fondo, que es lo único que se ve: la
    // negrita, el color de letra o la alineación de una celda sin nada no se
    // notan hasta que alguien escribe, y guardarlos era la mayor parte del
    // peso de un Excel importado (un formato armado para filas y filas que
    // todavía no se cargaron). Tampoco se cuentan en el aviso.
    if (vacia) {
      if (!f.bg) return;
      if (aprox) aproximados += 1;
      formato[XLSX.utils.encode_cell({ r, c })] = f;
      return;
    }
    if (xf) {
      const fuente = styles.Fonts[xf.fontId] || {};
      if (xf.fontId) {
        if (fuente.bold) f.bold = true;
        if (fuente.italic) f.italic = true;
        if (fuente.underline) f.subrayado = true;
        if (fuente.strike) contar("tachado");
        if (fuente.sz && fuenteBase.sz && fuente.sz !== fuenteBase.sz) contar("tamano");
        const hex = hexDeColor(fuente.color, wb);
        if (hex) {
          const { color, exacto } = textoMasCercano(hex);
          if (color) f.color = color;
          if (!exacto) aprox = true;
        }
      }
      const al = xf.alignment || {};
      if (ALINEACION[al.horizontal]) f.align = ALINEACION[al.horizontal];
      else if (al.horizontal && al.horizontal !== "general") contar("justificado");
      if (al.wrapText) contar("ajuste");
      if (al.textRotation && Number(al.textRotation)) contar("girado");
      if (xf.borderId && zip.bordes[xf.borderId]) contar("bordes");
    }
    if (aprox) aproximados += 1;
    if (Object.keys(f).length) formato[XLSX.utils.encode_cell({ r, c })] = f;
  };

  if (conXf) {
    for (const c of etiquetas(xml, /<c\b[^>]*>/)) {
      const ref = atributo(c, "r");
      const s = Number(atributo(c, "s") || 0);
      if (!ref || !s) continue; // sin `s` (o 0): el estilo de siempre
      const p = XLSX.utils.decode_cell(ref);
      una(p.r, p.c, ws[ref], styles.CellXf[s]);
    }
  } else {
    // Sin el XML (un .xls): sólo lo que SheetJS dejó en cada celda.
    for (const ref of Object.keys(ws || {})) {
      if (ref[0] === "!") continue;
      const celda = ws[ref];
      if (!celda || !celda.s) continue;
      const p = XLSX.utils.decode_cell(ref);
      una(p.r, p.c, celda, null);
    }
  }

  return {
    formato,
    aproximados,
    faltan,
    condicional: xml ? (xml.match(/<(?:\w+:)?conditionalFormatting\b/g) || []).length : 0,
    validacion: xml ? (xml.match(/<(?:\w+:)?dataValidation[\s>/]/g) || []).length : 0,
    // No se pudo saber la fuente ni la alineación (un .xls, o un libro leído
    // sin `bookFiles`): a lo sumo vinieron los fondos.
    soloFondos: !conXf,
  };
}
